import { useFirestore } from "reactfire";
import { useOrganization } from "../context/OrganizationContext";
import { useText } from "../context/TextContext";
import { COLLECTIONS, FIRST_INVOICE_NUMBER, INVOICE_COUNTER_DOC_ID } from "../utils/shared/constants";
import { Invoice } from "../models/shared/Invoice";
import { LineItem } from "../models/shared/LineItem";
import { Article } from "../models/shared/Article";
import { TimeRecord } from "../models/shared/TimeRecord";
import { collection, doc, getDocs, query, runTransaction, where } from "firebase/firestore";
import { fromFirebaseDocs } from "../utils/shared/firebase";
import { sortArticles } from "../utils/shared/article";
import { v4 as uuid } from 'uuid';
import { toast } from "react-toastify";

interface Period {
    startDate: Date;
    endDate: Date;
}

interface CreateInvoiceParams {
    dealId: string;
    period?: Period;
}

const getRecordOverlapMs = (record: TimeRecord, period?: Period): number => {
    const endTime = record.endAt || new Date();
    if (!period) {
        return Math.max(0, endTime.getTime() - record.startAt.getTime());
    }
    const overlapStart = Math.max(record.startAt.getTime(), period.startDate.getTime());
    const overlapEnd = Math.min(endTime.getTime(), period.endDate.getTime());
    return Math.max(0, overlapEnd - overlapStart);
};

export const useCreateInvoiceFromTimeReports = () => {
    const firestore = useFirestore();
    const { orgId } = useOrganization();
    const text = useText();

    return async ({ dealId, period }: CreateInvoiceParams): Promise<Invoice> => {
        const timeRecordsQuery = query(
            collection(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}`),
            where('dealId', '==', dealId),
        );
        const timeRecordsSnapshot = await getDocs(timeRecordsQuery);
        const timeRecords = fromFirebaseDocs<TimeRecord>(timeRecordsSnapshot.docs);

        const durationByActivityId: Record<string, number> = {};
        for (const record of timeRecords) {
            if (!record.activitySplits?.length) continue;

            const overlapMs = getRecordOverlapMs(record, period);
            if (overlapMs <= 0) continue;

            const recordTotalMs = (record.endAt || new Date()).getTime() - record.startAt.getTime();
            const overlapRatio = period ? (recordTotalMs > 0 ? overlapMs / recordTotalMs : 0) : 1;

            for (const split of record.activitySplits) {
                durationByActivityId[split.activityId] = (durationByActivityId[split.activityId] || 0) + split.durationMs * overlapRatio;
            }
        }

        const activityArticlesQuery = query(
            collection(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}`),
            where('isActivity', '==', true),
        );
        const activityArticlesSnapshot = await getDocs(activityArticlesQuery);
        const activityArticles = sortArticles(fromFirebaseDocs<Article>(activityArticlesSnapshot.docs))
            .filter(a => a.status !== 'archived');

        const lineItems: LineItem[] = [];
        for (const article of activityArticles) {
            const durationMs = durationByActivityId[article.id || ''] || 0;
            const quantity = Math.round((durationMs / 3600000) * 100) / 100;
            if (quantity <= 0) continue;

            lineItems.push({
                id: uuid(),
                articleId: article.id,
                description: article.name,
                quantity,
                unitPrice: article.perUnitPrice,
                vatRate: article.vatRate,
            });
        }

        const newInvoice: Invoice = {
            status: 'draft',
            createdAt: new Date(),
            updatedAt: new Date(),
            lineItems,
            ...(period ? { periodStart: period.startDate, periodEnd: period.endDate } : {}),
        };

        const invoicesPath = `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}/${COLLECTIONS.INVOICES}`;
        const newInvoiceRef = doc(collection(firestore, invoicesPath));
        const counterRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.COUNTERS}/${INVOICE_COUNTER_DOC_ID}`);

        const invoiceNumber = await runTransaction(firestore, async (transaction) => {
            const counterSnap = await transaction.get(counterRef);
            const nextNumber: number = counterSnap.data()?.next ?? FIRST_INVOICE_NUMBER;

            transaction.set(newInvoiceRef, { ...newInvoice, invoiceNumber: nextNumber });
            transaction.set(counterRef, { next: nextNumber + 1 }, { merge: true });

            return nextNumber;
        });

        toast.success(text.invoiceCreated);

        return { ...newInvoice, id: newInvoiceRef.id, invoiceNumber };
    };
};
